import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Star, Sun, Cloud, Flower2, Wrench } from 'lucide-react';
import { maintenanceAPI, landingAPI, BADGE_COLORS, LANDING_DEFAULTS, themeAPI, analyticsAPI } from '../../lib/api';
import { SeasonalDecorations } from '../layout/SeasonalDecorations';
import { SEO } from '../seo/SEO';

const SITE_VISUALS = {
  ms: { gradient: 'from-[#45AAF2] to-[#26DE81]', shadow: 'shadow-[#45AAF2]/30', hoverText: 'group-hover:text-[#45AAF2]', cta: 'text-[#45AAF2]', rotate: 'group-hover:rotate-3' },
  ds: { gradient: 'from-[#FF6B6B] to-[#FF9F43]', shadow: 'shadow-[#FF6B6B]/30', hoverText: 'group-hover:text-[#FF6B6B]', cta: 'text-[#FF6B6B]', rotate: 'group-hover:-rotate-3' }
};

const SiteCard = ({ siteKey, settings }) => {
  const visual = SITE_VISUALS[siteKey];
  const { icon_emoji, icon_image_url, title, subtitle, badges } = settings;
  return (
    <Link
      to={`/${siteKey}`}
      className={`landing-card landing-card-${siteKey} group`}
      data-testid={`landing-${siteKey}-card`}
    >
      <div className="relative z-10">
        <div className={`w-24 h-24 mx-auto mb-6 rounded-3xl bg-gradient-to-br ${visual.gradient} flex items-center justify-center shadow-lg ${visual.shadow} group-hover:scale-110 ${visual.rotate} transition-all duration-500 overflow-hidden`}>
          {icon_image_url ? (
            <img src={icon_image_url} alt={title} className="w-full h-full object-contain p-2" />
          ) : (
            <span className="text-4xl leading-none">{icon_emoji || '🏫'}</span>
          )}
        </div>
        <h2 className={`text-3xl font-bold text-[#2D3748] mb-3 text-center ${visual.hoverText} transition-colors`}>
          {title}
        </h2>
        <p className="text-[#64748B] text-center mb-6 text-lg">{subtitle}</p>
        {badges?.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            {badges.map((b, i) => {
              const colors = BADGE_COLORS[b.color_key] || BADGE_COLORS.blue;
              return (
                <span
                  key={i}
                  className="px-4 py-1.5 rounded-full text-sm font-semibold"
                  style={{ backgroundColor: colors.bg, color: colors.text }}
                  data-testid={`badge-${siteKey}-${i}`}
                >
                  {b.label}
                </span>
              );
            })}
          </div>
        )}
        <div className={`flex items-center justify-center gap-2 ${visual.cta} font-bold text-lg group-hover:gap-4 transition-all`}>
          Vstoupit
          <ArrowRight className="group-hover:translate-x-2 transition-transform" size={24} />
        </div>
      </div>
    </Link>
  );
};

export const LandingPage = () => {
  const [maintenance, setMaintenance] = useState({ is_active: false, message: '' });
  const [landing, setLanding] = useState(null);
  const [theme, setTheme] = useState({ theme: 'none', enabled: true });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    analyticsAPI.track('/', null);
    Promise.all([
      maintenanceAPI.get().then(r => r.data).catch(() => ({ is_active: false })),
      landingAPI.get().then(r => r.data).catch(() => null),
      themeAPI.get().then(r => r.data).catch(() => ({ theme: 'none', enabled: true }))
    ]).then(([m, l, t]) => {
      setMaintenance(m);
      setTheme(t);
      // Fallback na výchozí konfiguraci, pokud API není dostupné (starší backend, výpadek...)
      // Nebo doplní chybějící klíče z defaultu, aby karty vždy zobrazily kompletní obsah
      const safeLanding = {
        ms: { ...LANDING_DEFAULTS.ms, ...(l?.ms || {}) },
        ds: { ...LANDING_DEFAULTS.ds, ...(l?.ds || {}) }
      };
      // Pokud title přišel z DB jako prázdný řetězec, použij default
      if (!safeLanding.ms.title?.trim()) safeLanding.ms.title = LANDING_DEFAULTS.ms.title;
      if (!safeLanding.ds.title?.trim()) safeLanding.ds.title = LANDING_DEFAULTS.ds.title;
      setLanding(safeLanding);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#FFFDF7] via-[#FFF5E6] to-[#E8F4FD]">
        <div className="spinner" />
      </div>
    );
  }

  if (maintenance.is_active) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FFFDF7] via-[#FFF5E6] to-[#E8F4FD] relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-20 h-20 bg-[#FF6B6B]/20 rounded-full animate-float" />
          <div className="absolute top-40 right-20 w-16 h-16 bg-[#45AAF2]/20 rounded-full animate-bounce-slow" style={{ animationDelay: '0.5s' }} />
          <div className="absolute bottom-40 left-20 w-24 h-24 bg-[#26DE81]/20 rounded-full animate-float" style={{ animationDelay: '1s' }} />
          <Cloud className="absolute top-20 left-1/4 text-white/60" size={80} />
          <Cloud className="absolute top-10 left-1/2 text-white/40" size={60} />
        </div>
        <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 py-12">
          <div className="max-w-lg text-center animate-fade-in-up">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Wrench size={40} className="text-amber-600" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-[#2D3748] mb-4" data-testid="maintenance-title">
              Údržba webu
            </h1>
            <p className="text-lg text-[#64748B] mb-8 leading-relaxed" data-testid="maintenance-message">
              {maintenance.message || 'Probíhá údržba webu. Omlouváme se za komplikace, brzy budeme zpět!'}
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-[#64748B]">
              <Flower2 className="text-[#FF6B6B] animate-wiggle" size={20} />
              <span>MŠ Nová Role - Cestička & Chaloupka na vršku</span>
              <Flower2 className="text-[#26DE81] animate-wiggle" size={20} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFFDF7] via-[#FFF5E6] to-[#E8F4FD] relative overflow-hidden">
      <SEO
        title="MŠ Nová Role - Cestička a Chaloupka na vršku"
        description="Vítejte na oficiálních stránkách MŠ Cestička a Dětské skupiny Chaloupka na vršku v Nové Roli. Vyberte si zařízení, které vás zajímá."
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'MŠ Nová Role',
          url: typeof window !== 'undefined' ? window.location.origin : 'https://novy.msnovarole.cz',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Nová Role',
            postalCode: '362 25',
            addressCountry: 'CZ',
          },
        }}
      />
      {theme.enabled && <SeasonalDecorations theme={theme.theme} />}
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-20 h-20 bg-[#FF6B6B]/20 rounded-full animate-float" />
        <div className="absolute top-40 right-20 w-16 h-16 bg-[#45AAF2]/20 rounded-full animate-bounce-slow" style={{ animationDelay: '0.5s' }} />
        <div className="absolute bottom-40 left-20 w-24 h-24 bg-[#26DE81]/20 rounded-full animate-float" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-20 right-40 w-14 h-14 bg-[#FECA57]/30 rounded-full animate-bounce-slow" />
        <div className="absolute top-1/3 left-1/4 w-12 h-12 bg-[#A55EEA]/20 rounded-full animate-float" style={{ animationDelay: '1.5s' }} />
        <Star className="absolute top-32 right-1/4 text-[#FECA57] animate-wiggle" size={24} fill="#FECA57" />
        <Star className="absolute bottom-1/3 left-1/3 text-[#FF9F43] animate-wiggle" size={20} fill="#FF9F43" style={{ animationDelay: '0.3s' }} />
        <Star className="absolute top-1/2 right-10 text-[#45AAF2] animate-wiggle" size={18} fill="#45AAF2" style={{ animationDelay: '0.6s' }} />
        <Sun className="absolute top-10 right-10 text-[#FECA57] animate-spin" size={60} style={{ animationDuration: '20s' }} />
        <Cloud className="absolute top-20 left-1/4 text-white/60" size={80} />
        <Cloud className="absolute top-10 left-1/2 text-white/40" size={60} />
      </div>

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 py-12">
        <div className="text-center mb-12 animate-fade-in-up">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Flower2 className="text-[#FF6B6B] animate-wiggle" size={40} />
            <Heart className="text-[#FF6B6B] animate-bounce-slow" size={32} fill="#FF6B6B" />
            <Flower2 className="text-[#26DE81] animate-wiggle" size={40} style={{ animationDelay: '0.5s' }} />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
            <span className="rainbow-text">Vítejte!</span>
          </h1>
          <p className="text-xl sm:text-2xl text-[#64748B] font-semibold">
            Kam byste se rádi podívali?
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl w-full">
          {landing?.ms && <SiteCard siteKey="ms" settings={landing.ms} />}
          {landing?.ds && <SiteCard siteKey="ds" settings={landing.ds} />}
        </div>

        <div className="mt-12 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
          <Link
            to="/admin/login"
            className="text-[#64748B] hover:text-[#45AAF2] font-semibold transition-colors"
            data-testid="admin-link"
          >
            Přihlášení do administrace →
          </Link>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r from-[#FF6B6B] via-[#FECA57] via-[#26DE81] to-[#45AAF2]" />
      </div>
    </div>
  );
};

export default LandingPage;
